import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { TOP_BAR_HEIGHT } from "@/components/layout/components/constants";
import { MDSPanelModeBreakpoints, PanelMode } from "@/mds/foundations";
import { Button } from "react-aria-components";
import { TopBarDesktopSection } from "@/components/layout/components/top-bar/top-bar-desktop-section";
import {
  MDSIcon,
  MDSIconButton,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextOverflow,
  MDSToggle,
} from "@/mds";
import { SidebarMobile } from "@/components/layout/components/sidebar/sidebar-mobile";
import { useState } from "react";
import { TopBarProfileImage } from "@/components/layout/components/top-bar/top-bar-profile-image";
import { EventContext } from "@/domains/metrics/context";
import { AudioCaptureIndicator } from "@/components/layout/components/audio-capture";
import { BackForwardButtons } from "../back-forward-buttons";
import { clientEnvModule } from "@/modules/client-env";
import { TerminalOutcome } from "@/store/audio/pipeline/audio-pipeline.types";
import { SidebarVariant } from "../sidebar/constants";
import { isMac } from "@/domains/platform/isMac";
import { StoplightSpacer } from "@/components/layout/components/sidebar/sidebar-desktop-section";
import { resolveAudioRecordingDurationLabel } from "@/modules/audio-recording/recording-duration-label";
import { AgentOverlayEntryButton } from "@/pages/agent/components/agent-overlay-entry-button";
import { TopBarProactiveUpgradeButton } from "@/components/layout/components/top-bar/top-bar-proactive-upgrade-button";

interface TopBarProps {
  setIsClassicChatModalOpen: (b: boolean) => void;
  setIsImportModalOpen: (b: boolean) => void;
  currentSidebarVariant: SidebarVariant;
}

export const TopBar = observer(function TopBar({
  setIsClassicChatModalOpen,
  setIsImportModalOpen,
  currentSidebarVariant,
}: TopBarProps) {
  const { store, pageStore } = useAppStore();
  const isDesktop = clientEnvModule.isDesktop();
  const isMacOS = isMac();

  const [isPopUpBarOpen, setIsPopUpBarOpen] = useState(false);
  const [isSearchHovered, setIsSearchHovered] = useState(false);
  const togglePopUpBar = () => setIsPopUpBarOpen(prev => !prev);

  const isNarrowView = !!store.interface.matchesPanelModeBreakpoint.get(PanelMode.Narrow);
  const activeRecording = store.audio.activeRecording;
  const activeRecordingNoteId = store.audio.activeRecordingNoteId;
  const activeNoteRecording = activeRecordingNoteId
    ? store.audioRecordings.getNoteRecording({ noteId: activeRecordingNoteId })
    : null;
  const activeRecordingDurationLabel = resolveAudioRecordingDurationLabel({
    activeRecording,
    noteRecordings: activeNoteRecording
      ? [activeNoteRecording.recording, ...(activeNoteRecording.recordings ?? [])]
      : [],
    handoffTransition: store.audio.detectedMeetingHandoffTransition,
    noteId: activeRecordingNoteId,
    fallbackDurationLabel: activeRecording?.formattedDuration ?? "00:00",
  });
  const shouldShowAgenticSearchToggle =
    !isNarrowView && pageStore.quickSearchModal.isAgenticSearchAvailable;
  const shouldShowAgentOverlayEntry = store.agentOverlay.isEntryPointEnabled;
  const hasTransformPipelineError = (() => {
    if (!activeRecording || !activeRecordingNoteId) return false;
    const snapshot = store.audio.pipeline.getSnapshot(activeRecording.id);
    if (!snapshot || snapshot.terminalOutcome === null) return false;
    return (
      snapshot.terminalOutcome !== TerminalOutcome.Success &&
      snapshot.terminalOutcome !== TerminalOutcome.NoUsableAudioFound
    );
  })();
  const handleSearchClick = () => {
    pageStore.quickSearchModal.toggleIsOpen();
    setIsPopUpBarOpen(false);
  };

  const handleNewNoteClick = () => {
    store.notes.composeNewNote({ eventContext: EventContext.GlobalActions });
    setIsPopUpBarOpen(false);
  };

  const handleAudioCaptureClick = () => {
    /**
     * We fire this off asynchronously.
     * It will ensure that - if the user has somehow managed to get their audio recording into
     * an invalid state, it will be reset correctly.
     */
    void store.audio.ensureActiveRecordingStateIsValid();

    if (!store.audio.activeRecordingNoteId) return null;
    setIsPopUpBarOpen(false);
    store.navigation.goToNote({ noteId: store.audio.activeRecordingNoteId });
  };

  return (
    <>
      {isNarrowView && isPopUpBarOpen && (
        <SidebarMobile
          onClick={() => setIsPopUpBarOpen(false)}
          setIsClassicChatModalOpen={setIsClassicChatModalOpen}
          setIsImportModalOpen={setIsImportModalOpen}
        />
      )}
      <Wrapper>
        <LeftSection $isNarrow={isNarrowView}>
          {isDesktop && isMacOS && isNarrowView && <StoplightSpacer />}
          {isDesktop && currentSidebarVariant !== SidebarVariant.Expanded && (
            <BackForwardButtons
              navigateBack={store.navigation.goBack}
              navigateForward={store.navigation.goForward}
              canNavigateBack={store.navigation.canGoBack}
              canNavigateForward={store.navigation.canGoForward}
            />
          )}
          {/* Mobile-specific controls */}
          {isNarrowView && (
            <NarrowLeftButtons>
              <TopBarProfileImage togglePopUpBar={togglePopUpBar} />
              <MDSIconButton
                iconKind={MDSIconKind.ComposeInline}
                isDisabled={store.notes.newNoteIsBeingCreated}
                onClick={handleNewNoteClick}
                data-testid="compose-note-button"
              />
            </NarrowLeftButtons>
          )}
          <AudioCaptureIndicator
            expanded={!isNarrowView}
            onClick={handleAudioCaptureClick}
            audioRecording={activeRecording}
            isErrorState={hasTransformPipelineError}
            durationLabelOverride={activeRecordingDurationLabel}
          />
          <SearchButtonWrapper $isSearchHovered={isSearchHovered}>
            <SearchButton
              onClick={handleSearchClick}
              onMouseEnter={() => setIsSearchHovered(true)}
              onMouseLeave={() => setIsSearchHovered(false)}
            >
              <MDSIcon kind={MDSIconKind.Search} color={MDSIconColor.Placeholder} />
              <MDSText
                numberOfLines={1}
                overflow={MDSTextOverflow.Ellipsis}
                color={MDSTextColor.Tertiary}
              >
                {isNarrowView ? "Search" : "Search or ask your Mem"}
              </MDSText>
            </SearchButton>
            <SearchTrailingActions>
              <SearchShortcutTooltip $isVisible={isSearchHovered && !isNarrowView}>
                <KeyIcon>{isMacOS ? "⌘" : "Ctrl"}</KeyIcon>
                <KeyIcon>K</KeyIcon>
              </SearchShortcutTooltip>
              {shouldShowAgenticSearchToggle && (
                <AgenticToggleLabel>
                  <MDSToggle
                    checked={pageStore.quickSearchModal.isAgenticSearchEnabled}
                    onChange={event =>
                      pageStore.quickSearchModal.setAgenticSearchEnabled(event.target.checked)
                    }
                  />
                  <AgenticToggleEmoji aria-label="Agentic search">🤖</AgenticToggleEmoji>
                </AgenticToggleLabel>
              )}
              {shouldShowAgentOverlayEntry && <AgentOverlayEntryButton isNarrow={isNarrowView} />}
            </SearchTrailingActions>
          </SearchButtonWrapper>
          {!isNarrowView && <TopBarProactiveUpgradeButton />}
        </LeftSection>
        <RightSection>
          <TopBarDesktopSection />
        </RightSection>
      </Wrapper>
    </>
  );
});

const Wrapper = styled.div(({ theme }) => ({
  width: "100%",
  paddingRight: theme.spacing["2"],
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: `${TOP_BAR_HEIGHT}px`,
  WebkitAppRegion: "drag", // Makes the top bar draggable in desktop app
  overflow: "hidden",
  paddingBlock: theme.spacing["2"],
  flexShrink: 0,

  // Responsive styling for narrow view
  [`@media ${MDSPanelModeBreakpoints[PanelMode.Narrow]}`]: {
    flexWrap: "nowrap",
    gap: 0,
  },
}));

const SearchButtonWrapper = styled.div<{ $isSearchHovered: boolean }>`
  position: relative;
  height: 100%;
  flex-grow: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.borderRadius.max};
  background-color: ${({ theme, $isSearchHovered }) =>
    $isSearchHovered
      ? theme.colors.background.input.chrome.hover
      : theme.colors.background.input.chrome.default};
  transition: background-color 0.2s ease-in-out;
  -webkit-app-region: no-drag;

  @media ${MDSPanelModeBreakpoints[PanelMode.Narrow]} {
    margin: 0 8px;
  }
`;

const SearchButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 1 1 auto;
  gap: 4px;
  height: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.max};
  padding: 0 12px;
  -webkit-app-region: no-drag;
  min-width: 0;
  width: auto;

  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: ${({ theme }) => theme.lineHeights["paragraph-s"]};
  color: ${({ theme }) => theme.colors.text.primary};

  background-color: transparent;

  // Responsive styling for narrow view
  @media ${MDSPanelModeBreakpoints[PanelMode.Narrow]} {
    max-width: none;
  },
`;

const SearchTrailingActions = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: ${({ theme }) => theme.spacing["1"]};
  padding-right: 6px;
  -webkit-app-region: no-drag;
`;

const AgenticToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
`;

const AgenticToggleEmoji = styled.span`
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  line-height: 1;
`;

const LeftSection = styled.div<{ $isNarrow?: boolean }>`
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  overflow: hidden;
  flex-grow: 1;
  gap: ${({ theme, $isNarrow }) => ($isNarrow ? theme.spacing["0"] : theme.spacing["2"])};
`;

const NarrowLeftButtons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
  /* avoid macOS stoplights */
`;

// Right section container
const RightSection = styled.div`
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  overflow: hidden;
`;

// Search Shortcut Tooltip Component
const SearchShortcutTooltip = styled.div<{ $isVisible: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: ${({ $isVisible }) => ($isVisible ? "40px" : 0)};
  overflow: hidden;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  visibility: ${({ $isVisible }) => ($isVisible ? "visible" : "hidden")};
  transition:
    max-width 0.2s ease-in-out,
    opacity 0.2s ease-in-out,
    visibility 0.2s ease-in-out;
  pointer-events: none;
`;

const KeyIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1px solid ${({ theme }) => theme.colors.border.chrome};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.tertiary};
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
  padding: 1px;
`;
